import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router";
import { DetailsPopup } from "modules/common/components/form/DetailsPopup";
import { Block } from "modules/common/components/page/Block";
import { matchRoute } from "modules/common/services/routing";
import { WhenPath } from "modules/common/components/routing/WhenPath";
import { routes } from "modules/common/routes";
import { observer } from "mobx-react";
import { texts } from "modules/common/texts";
import { EMPTY_OBJECT_ID } from "modules/common/constants";
import { ProductionStagesStoreType } from "../models/production-stages-store";
import { ProductionStageDetails } from "./details/ProductionStageDetails";
import { ProductionStagesList } from "./list/ProductionStagesList";
import { menuItems } from "modules/root/components/menu/MainMenu";
import func from "modules/dictionaries/functionalities";
import { can } from "modules/session/auth/access";

import { PlanrButton } from "modules/common/components/planr/button/Button";
import styles from "./ProductionStagesPage.module.scss";
import { Search } from "modules/common/components/form/Search";
import { useStableNavigate } from "modules/common/components/routing/useStableNavigate";

const menu = menuItems();

/**
 * Образец перевода классовой страницы на функцию (правило в Rost.Ui/CLAUDE.md).
 *
 * Что было в классе и чем стало:
 * - `state.stageId` + `componentDidUpdate` по смене `location` → id вычисляется из
 *   адреса на каждом рендере, а загрузка карточки — эффект с зависимостью от него;
 * - `componentDidMount` → эффект без зависимостей от адреса (заголовок, список);
 * - `withNavigation` и пропы `navigate` / `location` → `useStableNavigate` и `useLocation`;
 * - методы-обработчики → `useCallback`: `navigate` стабилен, поэтому и они не меняются
 *   между переходами, как не менялись методы класса.
 */
const ProductionStagesPage = observer(({ store, access }: ProductionStagesPageProps) => {
  const navigate = useStableNavigate();
  const { pathname } = useLocation();
  const stageId = getSelectedProductionStageId(pathname);
  const canChange = can(func.PRODUCTIONSTAGE_EDIT, access);

  useEffect(() => {
    document.title = menu.dictionaries.nested.productionStages.label;
    store.list.load();
  }, [store.list]);

  // карточка следует за адресом: открыли другую стадию — загружаем её
  useEffect(() => {
    store.details.load(stageId);
  }, [store.details, stageId]);

  const onProductionStageSelected = useCallback(
    (id: string = "") => {
      navigate(id ? routes.dictionaries.productionStage.details(id) : routes.dictionaries.productionStage.path);
    },
    [navigate]
  );

  const onProductionStageAdd = useCallback(() => onProductionStageSelected(EMPTY_OBJECT_ID), [onProductionStageSelected]);

  const onProductionStageSaved = useCallback(() => {
    onProductionStageSelected("");
    store.list.load();
  }, [onProductionStageSelected, store.list]);

  const onProductionStageRemoved = useCallback(() => {
    store.list.load();
    navigate(routes.dictionaries.productionStage.path);
  }, [navigate, store.list]);

  return (
    <Block columned={true} className={styles.page}>
      <WhenPath path={routes.dictionaries.productionStage.details()}>
        <DetailsPopup title="Стадия строительства" onCancel={onProductionStageSelected}>
          <ProductionStageDetails
            canChange={canChange}
            productionStage={store.details}
            onSaved={onProductionStageSaved}
            onRemoved={onProductionStageRemoved}
          />
        </DetailsPopup>
      </WhenPath>
      <div className={styles.tools}>
        {canChange && (
          <PlanrButton
            type="secondary"
            size="small"
            icon="general-plus-big"
            onClick={onProductionStageAdd}
            style={{
              width: "310px",
              marginBottom: "32px",
              borderRadius: "120px",
            }}
          >{`${texts.add} стадию строительства`}</PlanrButton>
        )}
      </div>
      <div className={styles.mobileTools}>
        {canChange && <PlanrButton type="secondary" round icon="general-plus-big" onClick={onProductionStageAdd} />}
      </div>
      <div className={styles.right}>
        <Search query={store.list.query} onSearch={store.list.setQuery} />
      </div>
      <ProductionStagesList
        store={store.list}
        onSelected={onProductionStageSelected}
        onRemoved={onProductionStageRemoved}
      />
    </Block>
  );
});

export default ProductionStagesPage;

function getSelectedProductionStageId(pathname: string) {
  const match = matchRoute<{ stageId: string }>(pathname, routes.dictionaries.productionStage.details());

  return match && match.params ? match.params.stageId : "";
}

interface ProductionStagesPageProps {
  store: ProductionStagesStoreType;
  access: string[];
}
